import { useMediaQuery } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useFetchRepo } from '../api';
import {
  durationData,
  PeriodType,
  removeEmptyQueryParams,
  useDashboardStore,
  useQueryState,
} from '@devd-client/devd/components';
import { useAuth } from '@devd-client/devd/auth';
import { useTicketCards, QUERY_KEYS } from '../graphql';
import { ticketTypeFilterOptions } from '../helpers/home.utils';
import moment from 'moment';

export const useTicketDashboard = () => {
  const [isLargerThan1820] = useMediaQuery('(min-width: 1820px)');
  const [isLargeThan2120] = useMediaQuery('(min-width: 2120px)');

  const { data: repoList, isLoading: repoLoading } = useFetchRepo('jira');
  const [filterBy, setFilterBy] = useState<{
    value: string;
    label: string;
  }>({ value: 'team', label: 'Team' });
  const [repos, setRepos] = useState([]);

  const [startDate, setStartDate] = useQueryState('start');
  const [endDate, setEndDate] = useQueryState('end');
  const [duration, setDuration] = useQueryState('duration');
  const [team, setTeam] = useQueryState('team');
  const [sprintId, setSprintId] = useQueryState('sprintId');
  const [sprintName, setSprintName] = useQueryState('sprint');
  const [ticket, setTicket] = useQueryState('ticket');
  const [metricType, setMetricType] = useQueryState('tab');
  const [appState, dispatch] = useAuth();
  const [showForecast, setShowForecast] = useQueryState('showForecast');
  const [periodOptions, setPeriodOptions] =
    useState<PeriodType[]>(durationData);

  const { selectedTeam, selected, setSelectedTeam, setSelected } =
    useDashboardStore();

  const [type, setType] = useState<string>();

  const [ticketType, setTicketType] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);
  const [clickedTab, setClickedTab] = useState<string>();

  const { data, isFetching } = useTicketCards(
    selected.startDate,
    selected.endDate,
    selected?.sprintId,
    filterBy.value === 'project' ? '' : selectedTeam.value,
    filterBy.value === 'team'
      ? []
      : repos.map((el: { value: string; label: string }) => el.value),
    ticketType?.length === 1 ? ticketType[0].value : 'all',
    showForecast === 'true'
  );

  useEffect(() => {
    if (!data) return;
    if (!clickedTab && !type) {
      setClickedTab(data[0]?.chartKey);
      setType(data[0]?.chartKey);
    }
  }, [data]);

  useEffect(() => {
    if (typeof selected !== 'undefined') {
      selected.startDate && setStartDate(selected.startDate);
      selected.endDate && setEndDate(selected.endDate);
      selected.duration && setDuration(selected.duration);
      selected?.sprintId === ''
        ? removeEmptyQueryParams({ sprintId: '' })
        : setSprintId(selected?.sprintId);
      selected?.sprintName === ''
        ? removeEmptyQueryParams({ sprint: '' })
        : setSprintName(selected?.sprintName);
    }

    if (ticketType) {
      ticketType?.length > 1
        ? setTicket('all')
        : setTicket(ticketType[0]?.value);
    }

    if (typeof selectedTeam !== 'undefined') {
      selectedTeam.label === 'Organisation'
        ? setTeam('Organisation')
        : setTeam(selectedTeam.value);
    }

    if (type) {
      setMetricType(type);
    }
  }, [selected, selectedTeam.value, ticketType, type]);

  useEffect(() => {
    team &&
      setSelectedTeam(
        team === 'Organisation'
          ? { value: '', label: 'Organisation' }
          : { value: team, label: team }
      );

    metricType && setType(metricType);
    metricType && setClickedTab(metricType);

    ticket && ticket === 'others'
      ? setTicketType([ticketTypeFilterOptions[2]])
      : ticket === 'bug'
      ? setTicketType([ticketTypeFilterOptions[1]])
      : ticket === 'task'
      ? setTicketType([ticketTypeFilterOptions[0]])
      : ticket === 'all'
      ? setTicketType(ticketTypeFilterOptions)
      : setTicketType([]);

    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  return {
    ticketType,
    setTicketType,
    filterBy,
    setFilterBy,
    selected,
    selectedTeam,
    setSelectedTeam,
    appState,
    dispatch,
    repos,
    setRepos,
    repoList,
    repoLoading,
    isLargeThan2120,
    isLargerThan1820,
    QUERY_KEYS,
    data,
    isFetching,
    type,
    setType,
    clickedTab,
    setClickedTab,
    showForecast,
    setShowForecast,
  };
};
